import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { X, RotateCcw, Settings2 } from 'lucide-react'
import { Switch } from '@/admin/components/Switch'
import type { BoothOverrides } from '@/lib/boothOverrides'
import type { EventConfig } from '@/types'

interface QuickSettingsProps {
  event: EventConfig
  overrides: BoothOverrides
  onChange: (o: BoothOverrides) => void
  onClose: () => void
  onReset: () => void
  onTriggerZigbeeLight?: (state: 'ON' | 'OFF') => void
}

const RESOLUTION_PRESETS = [
  { label: '4K · 30fps',    width: 3840, height: 2160, fps: 30 },
  { label: '1440p · 60fps', width: 2560, height: 1440, fps: 60 },
  { label: '1440p · 30fps', width: 2560, height: 1440, fps: 30 },
  { label: '1080p · 60fps', width: 1920, height: 1080, fps: 60 },
  { label: '1080p · 30fps', width: 1920, height: 1080, fps: 30 },
  { label: '896p · 60fps',  width: 1600, height:  896, fps: 60 },
  { label: '896p · 30fps',  width: 1600, height:  896, fps: 30 },
  { label: '720p · 60fps',  width: 1280, height:  720, fps: 60 },
  { label: '720p · 30fps',  width: 1280, height:  720, fps: 30 },
  { label: '576p · 60fps',  width: 1024, height:  576, fps: 60 },
  { label: '576p · 30fps',  width: 1024, height:  576, fps: 30 },
  { label: '540p · 60fps',  width:  960, height:  540, fps: 60 },
  { label: '540p · 30fps',  width:  960, height:  540, fps: 30 },
] as const

function Row({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 border-b border-white/5 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white">{label}</div>
        {description && <div className="text-xs text-white/40 mt-0.5 leading-tight">{description}</div>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center gap-3">
        <div className="h-px flex-1 bg-white/10" />
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/30">{title}</span>
        <div className="h-px flex-1 bg-white/10" />
      </div>
      <div className="rounded-xl bg-white/5 px-4">{children}</div>
    </div>
  )
}

function RangeRow({
  label,
  description,
  value,
  min,
  max,
  step = 1,
  unit = '',
  onChange,
}: {
  label: string
  description?: string
  value: number
  min: number
  max: number
  step?: number
  unit?: string
  onChange: (v: number) => void
}) {
  return (
    <div className="py-1.5 border-b border-white/5 last:border-0">
      <div className="flex items-center justify-between mb-0.5">
        <div>
          <div className="text-sm font-semibold text-white">{label}</div>
          {description && <div className="text-xs text-white/40 mt-0.5">{description}</div>}
        </div>
        <span className="text-sm font-mono text-primary tabular-nums">
          {value}{unit}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onClick={(e) => e.stopPropagation()}
        className="w-full h-1.5 appearance-none rounded-full bg-white/10 accent-primary cursor-pointer"
      />
      <div className="flex justify-between mt-0.5">
        <span className="text-[10px] text-white/20">{min}{unit}</span>
        <span className="text-[10px] text-white/20">{max}{unit}</span>
      </div>
    </div>
  )
}

export default function QuickSettings({ event, overrides, onChange, onClose, onReset, onTriggerZigbeeLight }: QuickSettingsProps) {
  const cap = event.capture
  const shr = event.sharing
  const att = event.virtualAttendant

  const set = (patch: Partial<BoothOverrides>) => onChange({ ...overrides, ...patch })

  const [propsList, setPropsList] = useState<string[]>([])

  useEffect(() => {
    fetch('/api/props')
      .then(r => r.json())
      .then(data => setPropsList(data))
      .catch(err => console.error('Failed to fetch props:', err))
  }, [])

  const get = <K extends keyof BoothOverrides>(key: K, fallback: NonNullable<BoothOverrides[K]>): NonNullable<BoothOverrides[K]> =>
    (overrides[key] !== undefined ? overrides[key] : fallback) as NonNullable<BoothOverrides[K]>

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 28, stiffness: 300 }}
      onClick={(e) => e.stopPropagation()}
      className="absolute right-0 top-0 bottom-0 z-[200] w-[380px] flex flex-col"
    >
      <div className="flex flex-col h-full rounded-l-xl bg-black/75 border-l border-t border-b border-white/10 shadow-[0_0_80px_rgba(0,0,0,0.8)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/20 border border-primary/30">
              <Settings2 size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-tight">Quick Settings</h2>
              <p className="text-xs text-white/40">Operator overrides, not saved</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1 custom-scrollbar">
          {/* Camera */}
          <Section title="Camera">
            <div className="py-1.5 border-b border-white/5 last:border-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white">Resolution</div>
                  <div className="text-xs text-white/40 mt-0.5 leading-tight">Change camera mode</div>
                </div>
                <select
                  value={(() => {
                    const r = get('resolution', event.camera.resolution)
                    const f = get('frameRate', event.camera.frameRate ?? 30)
                    const p = RESOLUTION_PRESETS.find(p => p.width === r.width && p.height === r.height && p.fps === f)
                    return p ? `${p.width}x${p.height}@${p.fps}` : 'custom'
                  })()}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === 'custom') return
                    const [res, fps] = val.split('@')
                    const [w, h] = res.split('x').map(Number)
                    set({
                      resolution: { width: w, height: h },
                      frameRate: Number(fps)
                    })
                  }}
                  className="rounded-lg border border-white/10 bg-white/10 px-2 py-1 text-sm text-white focus:outline-none focus:border-primary"
                >
                  {RESOLUTION_PRESETS.map((p) => (
                    <option key={`${p.width}x${p.height}@${p.fps}`} value={`${p.width}x${p.height}@${p.fps}`} className="bg-zinc-900 text-white">
                      {p.label}
                    </option>
                  ))}
                  <option value="custom" className="bg-zinc-900 text-white">Custom</option>
                </select>
              </div>
            </div>
            <Row label="Debug Overlay" description="Show actual resolution on screen">
              <Switch
                checked={get('debugOverlayResolution', false)}
                onCheckedChange={(v) => set({ debugOverlayResolution: v })}
              />
            </Row>
          </Section>

          {/* Effects */}
          <Section title="Effects">

            <Row
              label="Face Tracking AI"
              description="Enable AR face tracking (Beta)"
            >
              <Switch
                checked={get('advancedBeautyFilter', event.effects.faceTracking?.enabled || false)}
                onCheckedChange={(v) => set({ advancedBeautyFilter: v })}
              />
            </Row>
            {get('advancedBeautyFilter', event.effects.faceTracking?.enabled || false) && (
              <div className="space-y-2 px-4 py-2">
                <div className="space-y-1">
                  <label className="text-xs font-medium">Select Prop</label>
                  <select 
                    value={get('faceTrackingProp', '')} 
                    onChange={(e) => set({ faceTrackingProp: e.target.value })} 
                    className="w-full p-1.5 text-sm border rounded-md bg-background text-foreground"
                  >
                    <option value="">None</option>
                    {propsList.map(prop => (
                      <option key={prop} value={`/props/${prop}`}>{prop}</option>
                    ))}
                    <option value="custom">Custom URL...</option>
                  </select>
                </div>
                
                {(get('faceTrackingProp', '') === 'custom' || (!get('faceTrackingProp', '').startsWith('/props/') && get('faceTrackingProp', '') !== '')) && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium">Custom Prop URL</label>
                    <input 
                      type="text" 
                      value={get('faceTrackingProp', '') === 'custom' ? '' : get('faceTrackingProp', '')} 
                      onChange={(e) => set({ faceTrackingProp: e.target.value })} 
                      onPaste={(e) => {
                        const items = e.clipboardData.items
                        for (let i = 0; i < items.length; i++) {
                          if (items[i].type.indexOf('image') !== -1) {
                            const blob = items[i].getAsFile()
                            if (blob) {
                              const reader = new FileReader()
                              reader.onload = (event) => {
                                const base64 = event.target?.result as string
                                set({ faceTrackingProp: base64 })
                              }
                              reader.readAsDataURL(blob)
                              e.preventDefault()
                              break
                            }
                          }
                        }
                      }}
                      className="w-full p-1.5 text-sm border rounded-md bg-background text-foreground"
                      placeholder="Paste URL or Image"
                    />
                  </div>
                )}
              </div>
            )}

          </Section>

          {/* Capture */}
          <Section title="Capture">
            <Row label="Pose Suggestions" description="Show example poses before countdown">
              <Switch
                checked={get('poseSuggestions', cap.poseSuggestions?.enabled ?? true)}
                onCheckedChange={(v) => set({ poseSuggestions: v })}
              />
            </Row>
            {get('poseSuggestions', cap.poseSuggestions?.enabled ?? true) && (
              <RangeRow
                label="Suggestion Display Time"
                value={get('poseSuggestionDelay', cap.poseSuggestions?.showSecondsBefore ?? 2)}
                min={0}
                max={16}
                unit="s"
                onChange={(v) => set({ poseSuggestionDelay: v })}
              />
            )}

            <RangeRow
              label="Countdown Duration"
              description="Seconds before the first capture"
              value={get('countdownDelay', cap.photo.delayBeforeFirst)}
              min={1}
              max={20}
              unit="s"
              onChange={(v) => set({ countdownDelay: v })}
            />
            <RangeRow
              label="Intermediate Preview Time"
              description="How long the photo preview stays between shots"
              value={get('reviewTime', cap.photo.reviewTime)}
              min={0}
              max={60}
              unit="s"
              onChange={(v) => set({ reviewTime: v })}
            />
            <RangeRow
              label="End Screen Timeout"
              description="How long the screen with the QR code stays"
              value={get('finishScreenTimeout', cap.finishScreenTimeout)}
              min={5}
              max={120}
              unit="s"
              onChange={(v) => set({ finishScreenTimeout: v })}
            />
          </Section>

          {/* Sharing */}
          <Section title="Sharing">
            <Row label="QR Code" description="Show scan-to-download QR on share screen">
              <Switch
                checked={get('qrEnabled', shr.qrEnabled)}
                onCheckedChange={(v) => set({ qrEnabled: v })}
              />
            </Row>
            <Row label="Online Gallery" description="Allow guests to access the gallery">
              <Switch
                checked={get('galleryEnabled', shr.galleryEnabled)}
                onCheckedChange={(v) => set({ galleryEnabled: v })}
              />
            </Row>
          </Section>

          {/* Display */}
          <Section title="Display">
            <Row label="Start Button Image" description="Show static image on welcome screen">
              <Switch
                checked={get('startButtonImageEnabled', event.screens.welcome.startButtonImage?.enabled ?? false)}
                onCheckedChange={(v) => set({ startButtonImageEnabled: v })}
              />
            </Row>
            <div className="py-1.5 border-b border-white/5 last:border-0">
              <button
                onClick={() => {
                  if (document.fullscreenElement) {
                    document.exitFullscreen().catch(err => console.error(err))
                  }
                }}
                className="w-full py-2 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-white text-sm font-semibold transition-all border border-white/10"
              >
                Exit Full Screen
              </button>
            </div>
          </Section>

          {/* Attendant */}
          <Section title="Attendant">
            <Row label="Virtual Attendant" description="Audio/video cues during capture">
              <Switch
                checked={get('virtualAttendant', att.enabled)}
                onCheckedChange={(v) => set({ virtualAttendant: v })}
              />
            </Row>
          </Section>

          {/* Zigbee */}
          <Section title="Zigbee Light">
            <Row label="Enable Photo Light" description="Turn on light during photo capture">
              <Switch
                checked={get('zigbeeLightEnabled', event.capture.zigbee?.enabled ?? false)}
                onCheckedChange={(v) => set({ zigbeeLightEnabled: v })}
              />
            </Row>
            {get('zigbeeLightEnabled', event.capture.zigbee?.enabled ?? false) && onTriggerZigbeeLight && (
              <div className="py-2 border-t border-white/5 flex items-center justify-between gap-4">
                <span className="text-xs text-white/40 leading-tight">Manual Flashlight</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => onTriggerZigbeeLight('OFF')}
                    className="px-3 py-1 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-lg text-xs font-semibold transition-all border border-white/10"
                  >
                    OFF
                  </button>
                  <button
                    onClick={() => onTriggerZigbeeLight('ON')}
                    className="px-3 py-1 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg text-xs font-semibold transition-all shadow-md"
                  >
                    ON
                  </button>
                </div>
              </div>
            )}
          </Section>

          {/* Override indicator */}
          {Object.keys(overrides).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl bg-amber-500/10 border border-amber-500/20 px-4 py-3 flex items-center gap-3"
            >
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-300">Active overrides</p>
                <p className="text-xs text-amber-300/60">{Object.keys(overrides).length} setting{Object.keys(overrides).length > 1 ? 's' : ''} changed from defaults</p>
              </div>
            </motion.div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/10 flex gap-3 shrink-0">
          <button
            onClick={onReset}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-sm font-semibold transition-all border border-white/10"
          >
            <RotateCcw size={14} />
            Reset to Defaults
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-bold transition-all shadow-lg"
          >
            Close
          </button>
        </div>
      </div>
    </motion.div>
  )
}
